import type { ReactNode } from 'react';
import {
	Accordion,
	AccordionItem,
	AccordionButton,
	AccordionPanel,
	Box,
	Divider,
	Flex,
	Heading,
	Link,
	ListItem,
	OrderedList,
	Table,
	TableContainer,
	Tbody,
	Td,
	Text,
	Th,
	Thead,
	Tr,
	UnorderedList,
} from '@chakra-ui/react';
import { Plus, Minus } from 'lucide-react';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';
import { useIsMobile } from '../hooks/useIsMobile';

const MotionBox = motion(Box);

export interface FAQTableData {
	head: string[];
	rows: string[][]; // first column is styled as the row label
}

// An answer is a markdown string, or an array mixing markdown strings and tables
export type FAQAnswer = string | Array<string | { table: FAQTableData }>;

export interface FAQItem {
	question: string;
	answer: FAQAnswer;
}

interface FAQProps {
	items: FAQItem[];
	headline?: string;
	subtitle?: string;
	defaultIndex?: number;
	// Hide the section header when the page already has its own
	showHeader?: boolean;
	// Shown centered under the questions (e.g. a CTA to the full FAQ page)
	footer?: ReactNode;
}

// Question titles are <h3>, so headings inside answers render as <h4>.
// Markdown #, ##, ### map to three visual sizes.
const answerHeading = (fontSize: { base: string; md: string }) =>
	({ children }: { children?: ReactNode }) => (
		<Heading
			as="h4"
			fontFamily={"Martel"}
			fontWeight={700}
			fontSize={fontSize}
			color={"white.500"}
			mt={6}
			mb={3}
			_first={{ mt: 0 }}
		>
			{children}
		</Heading>
	);

// Maps markdown elements to Chakra components styled to match the site
const markdownComponents: Components = {
	h1: answerHeading({ base: "xl", md: "2xl" }),
	h2: answerHeading({ base: "lg", md: "xl" }),
	h3: answerHeading({ base: "md", md: "lg" }),
	h4: answerHeading({ base: "md", md: "md" }),
	p: ({ children }) => (
		<Text mb={4} _last={{ mb: 0 }}>
			{children}
		</Text>
	),
	strong: ({ children }) => (
		<Text as="strong" fontWeight={700} color={"white.500"}>
			{children}
		</Text>
	),
	em: ({ children }) => (
		<Text as="em" fontStyle={"italic"}>
			{children}
		</Text>
	),
	ul: ({ children }) => (
		<UnorderedList ml={6} mb={4} spacing={2} _last={{ mb: 0 }}>
			{children}
		</UnorderedList>
	),
	ol: ({ children }) => (
		<OrderedList ml={6} mb={4} spacing={2} _last={{ mb: 0 }}>
			{children}
		</OrderedList>
	),
	li: ({ children }) => (
		<ListItem sx={{ "&::marker": { color: "#D0A144" } }}>{children}</ListItem>
	),
	a: ({ href, children }) => (
		<Link
			href={href}
			color={"yellow.100"}
			textDecoration={"underline"}
			isExternal={!!href?.startsWith("http")}
		>
			{children}
		</Link>
	),
	hr: () => <Divider my={6} borderColor={"whiteAlpha.300"} />,
	blockquote: ({ children }) => (
		<Box borderLeft={"3px solid"} borderLeftColor={"yellow.100"} pl={4} my={4}>
			{children}
		</Box>
	),
};

function AnswerTable({ head, rows }: FAQTableData) {
	return (
		<TableContainer
			mb={4}
			_last={{ mb: 0 }}
			border={"1px solid"}
			borderColor={"whiteAlpha.300"}
			borderRadius={"md"}
			whiteSpace={"normal"}
		>
			<Table variant={"simple"} size={"sm"}>
				<Thead backgroundColor={"whiteAlpha.100"}>
					<Tr>
						{head.map((cell, c) => (
							<Th
								key={c}
								fontFamily={"Montserrat"}
								fontWeight={600}
								fontSize={"sm"}
								textTransform={"none"}
								letterSpacing={"normal"}
								color={"white.500"}
								borderColor={"whiteAlpha.300"}
								whiteSpace={"normal"}
								verticalAlign={"top"}
								py={3}
							>
								{cell}
							</Th>
						))}
					</Tr>
				</Thead>
				<Tbody>
					{rows.map((row, r) => (
						<Tr key={r}>
							{row.map((cell, c) => (
								<Td
									key={c}
									fontFamily={"Montserrat"}
									fontWeight={c === 0 ? 600 : 500}
									fontSize={"sm"}
									color={c === 0 ? "white.500" : "grey.100"}
									borderColor={"whiteAlpha.300"}
									whiteSpace={"normal"}
									verticalAlign={"top"}
									py={3}
								>
									{cell}
								</Td>
							))}
						</Tr>
					))}
				</Tbody>
			</Table>
		</TableContainer>
	);
}

function renderAnswer(answer: FAQAnswer): ReactNode {
	const parts = typeof answer === "string" ? [answer] : answer;
	return parts.map((part, i) =>
		typeof part === "string" ? (
			<ReactMarkdown key={i} components={markdownComponents}>
				{part}
			</ReactMarkdown>
		) : (
			<AnswerTable key={i} {...part.table} />
		)
	);
}

export default function FAQ({
	items,
	headline = "Frequently Asked Questions",
	subtitle,
	defaultIndex,
	showHeader = true,
	footer,
}: FAQProps) {
	const isMobile: boolean = useIsMobile();

	return (
		<Flex
			width={"100%"}
			justifyContent={"center"}
			pt={showHeader ? (isMobile ? 12 : 20) : 0}
			pb={isMobile ? 12 : 20}
			px={isMobile ? 4 : 8}
		>
			<MotionBox
				width={"100%"}
				maxWidth={"900px"}
				initial={{ opacity: 0, y: 50 }}
				whileInView={{ opacity: 1, y: 0 }}
				viewport={{ once: true, amount: 0.2 }}
				transition={{ duration: 0.6, ease: "easeOut" }}
			>
				{/* Heading */}
				{showHeader && (
					<Box textAlign={"center"} mb={isMobile ? 8 : 12}>
						<Heading
							as="h2"
							fontFamily={"Martel"}
							fontWeight={800}
							fontSize={isMobile ? "2xl" : "4xl"}
							color={"white.500"}
						>
							{headline}
						</Heading>
						{subtitle && (
							<Text
								mt={4}
								fontFamily={"Montserrat"}
								fontWeight={500}
								fontSize={isMobile ? "sm" : "md"}
								color={"grey.100"}
								maxWidth={"600px"}
								mx={"auto"}
							>
								{subtitle}
							</Text>
						)}
					</Box>
				)}

				{/* Accordion */}
				<Accordion
					allowToggle
					defaultIndex={defaultIndex}
					display={"flex"}
					flexDirection={"column"}
					gap={3}
				>
					{items.map((item, index) => (
						<AccordionItem
							key={`${index}-${item.question}`}
							border={"none"}
							borderRadius={"md"}
							backgroundColor={"blue.400"}
							overflow={"hidden"}
						>
							{({ isExpanded }) => (
								<Box
									borderLeft={"3px solid"}
									borderLeftColor={isExpanded ? "yellow.100" : "transparent"}
									transition={"border-color 0.2s ease"}
								>
									<h3>
										<AccordionButton
											py={5}
											px={isMobile ? 4 : 6}
											gap={4}
											_hover={{ backgroundColor: "whiteAlpha.100" }}
											_focusVisible={{
												boxShadow: "inset 0 0 0 2px #D0A144",
												outline: "none",
											}}
										>
											<Text
												flex={1}
												textAlign={"left"}
												fontFamily={"Martel"}
												fontWeight={700}
												fontSize={isMobile ? "md" : "lg"}
												color={"white.500"}
											>
												{item.question}
											</Text>
											{isExpanded ? (
												<Minus size={22} color={"#D0A144"} />
											) : (
												<Plus size={22} color={"#D0A144"} />
											)}
										</AccordionButton>
									</h3>
									<AccordionPanel
										pt={0}
										pb={6}
										px={isMobile ? 4 : 6}
										fontFamily={"Montserrat"}
										fontWeight={500}
										fontSize={isMobile ? "sm" : "md"}
										lineHeight={1.7}
										color={"grey.100"}
									>
										{renderAnswer(item.answer)}
									</AccordionPanel>
								</Box>
							)}
						</AccordionItem>
					))}
				</Accordion>

				{/* Footer */}
				{footer && (
					<Flex justifyContent={"center"} marginTop={isMobile ? 8 : 10}>
						{footer}
					</Flex>
				)}
			</MotionBox>
		</Flex>
	);
}